import React, { useState, useEffect } from "react";

import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

import { testimonials, Testimonial } from "../data/testimonials";

type SlideDirection =
  | ""
  | "slide-left"
  | "slide-right"
  | "slide-in-left"
  | "slide-in-right";

const TestimonialsCarousel: React.FC = () => {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] =
    useState<number>(0);
  const [isSliding, setIsSliding] = useState<boolean>(false);
  const [slideDirection, setSlideDirection] = useState<SlideDirection>("");

  // Auto-flip carousel every 6 seconds
  useEffect(() => {
    if (isSliding) return; // Don't start new interval while sliding

    const interval = setInterval(() => {
      if (!isSliding) {
        setIsSliding(true);
        setSlideDirection("slide-right");

        setTimeout(() => {
          setCurrentTestimonialIndex(
            (prev) => (prev + 1) % testimonials.length
          );
          setSlideDirection("slide-in-left");

          setTimeout(() => {
            setIsSliding(false);
            setSlideDirection("");
          }, 500);
        }, 250);
      }
    }, 6000);

    return () => clearInterval(interval);
  }, [currentTestimonialIndex, isSliding]);

  const goToPreviousTestimonial = (): void => {
    if (isSliding) return; // Prevent multiple clicks during animation

    setIsSliding(true);
    setSlideDirection("slide-left");

    setTimeout(() => {
      setCurrentTestimonialIndex((prev) =>
        prev === 0 ? testimonials.length - 1 : prev - 1
      );
      setSlideDirection("slide-in-right");

      setTimeout(() => {
        setIsSliding(false);
        setSlideDirection("");
      }, 500);
    }, 250);
  };

  const goToNextTestimonial = (): void => {
    if (isSliding) return; // Prevent multiple clicks during animation

    setIsSliding(true);
    setSlideDirection("slide-right");

    setTimeout(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
      setSlideDirection("slide-in-left");

      setTimeout(() => {
        setIsSliding(false);
        setSlideDirection("");
      }, 500);
    }, 250);
  };

  const goToTestimonial = (index: number): void => {
    if (isSliding || index === currentTestimonialIndex) return;

    setIsSliding(true);
    const direction: SlideDirection =
      index > currentTestimonialIndex ? "slide-right" : "slide-left";
    const slideInDirection: SlideDirection =
      index > currentTestimonialIndex ? "slide-in-left" : "slide-in-right";

    setSlideDirection(direction);

    setTimeout(() => {
      setCurrentTestimonialIndex(index);
      setSlideDirection(slideInDirection);

      setTimeout(() => {
        setIsSliding(false);
        setSlideDirection("");
      }, 500);
    }, 250);
  };

  const currentTestimonial: Testimonial = testimonials[currentTestimonialIndex];

  return (
    <div className="max-w-4xl mx-auto w-full">
      <h3 className="text-3xl font-semibold text-purple-800 mb-4 text-center font-display">
        What other Cohoppers are saying
      </h3>

      {/* Carousel Container */}
      <div className="relative px-4 sm:px-8">
        {/* Navigation Buttons - Hidden on small screens, shown on larger screens */}
        <button
          onClick={goToPreviousTestimonial}
          className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-8 z-10 w-12 h-12 bg-white/90 border border-pink-200 rounded-full items-center justify-center text-purple-600 hover:bg-white hover:border-purple-300 transition-all duration-300 shadow-lg"
        >
          <FaChevronLeft />
        </button>

        <button
          onClick={goToNextTestimonial}
          className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-8 z-10 w-12 h-12 bg-white/90 border border-pink-200 rounded-full items-center justify-center text-purple-600 hover:bg-white hover:border-purple-300 transition-all duration-300 shadow-lg"
        >
          <FaChevronRight />
        </button>

        {/* Mobile Navigation - Positioned over the testimonial */}
        <div className="sm:hidden absolute inset-x-0 top-1/2 -translate-y-1/2 z-10 flex justify-between px-2 pointer-events-none">
          <button
            onClick={goToPreviousTestimonial}
            className="w-8 h-8 bg-white/95 border border-pink-200 rounded-full flex items-center justify-center text-purple-600 hover:bg-white hover:border-purple-300 transition-all duration-300 shadow-lg pointer-events-auto"
          >
            <FaChevronLeft className="text-xs" />
          </button>

          <button
            onClick={goToNextTestimonial}
            className="w-8 h-8 bg-white/95 border border-pink-200 rounded-full flex items-center justify-center text-purple-600 hover:bg-white hover:border-purple-300 transition-all duration-300 shadow-lg pointer-events-auto"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>

        {/* Testimonial Card */}
        <div className="py-4 min-h-[400px] flex items-center">
          <div
            className={`w-full bg-white/80 backdrop-blur-lg rounded-2xl p-8 border border-pink-200 shadow-lg hover:shadow-2xl hover:border-purple-300 hover:-translate-y-2 transition-all duration-300 cursor-pointer ${
              slideDirection === "slide-left"
                ? "transform -translate-x-full opacity-0"
                : slideDirection === "slide-right"
                  ? "transform translate-x-full opacity-0"
                  : slideDirection === "slide-in-left"
                    ? "transform translate-x-0 opacity-100"
                    : slideDirection === "slide-in-right"
                      ? "transform translate-x-0 opacity-100"
                      : "transform translate-x-0 opacity-100"
            }`}
            style={{
              transition:
                "transform 0.5s ease-in-out, opacity 0.5s ease-in-out",
            }}
          >
            <div className="flex flex-col items-center text-center space-y-6">
              {/* Name */}
              <h4 className="text-xl font-semibold text-purple-700 hover:text-purple-800 transition-colors duration-300">
                {currentTestimonial.name}
              </h4>

              {/* Testimonial Content */}
              <p className="text-gray-600 leading-relaxed italic text-lg hover:text-gray-700 transition-colors duration-300">
                &quot;{currentTestimonial.content}&quot;
              </p>
            </div>
          </div>
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center space-x-2 mt-6">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToTestimonial(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentTestimonialIndex
                  ? "bg-gradient-to-r from-pink-400 to-purple-500 shadow-md"
                  : "bg-gray-300 hover:bg-gray-400"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TestimonialsCarousel;
